import { useQuery } from "@tanstack/react-query"
import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { api, type ConfigVersionSummary, type Registry } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Flag } from "@/components/Flag.js"
import { PageHeader, Section } from "@/components/Page.js"
import { Badge } from "@/components/ui/badge.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.js"
import { plural } from "@/lib/plural.js"
import { cn } from "@/lib/utils.js"
import { type ConfigChange, describeChanges } from "./config/diff.js"
import { useDraft } from "./config/draft.js"
import {
  type EditorCountry,
  type EditorQuestion,
  enabledCount,
  withEnabledOrder,
} from "./config/model.js"
import { ProviderList } from "./config/Providers.js"
import { PublishPanel } from "./config/PublishPanel.js"
import { RulesPane } from "./config/RulesPane.js"
import { Scoreboard } from "./config/Scoreboard.js"
import { formatDate } from "./Entity.js"

/**
 * Editing configuration is a production change: it takes effect on the next
 * decision, with no cache to purge. So this screen publishes a **new version**
 * rather than editing one — every decision records the version that produced
 * it, and editing in place would leave history pointing at something that no
 * longer exists.
 *
 * **It is a list and an editor, not an article.** Every rule is one line a
 * person can scan — where it runs, what it is called, what it does — and the
 * one they pick opens beside the list with every setting laid out and
 * explained in the registry's own words. The screen explains nothing it can
 * instead show; what a person needs to know about the order, the cost of a
 * source or what a veto is belongs to whoever is trusted with this panel, and
 * what a *rule* has to say for itself is one click away, with room.
 */
export function ConfigurationScreen() {
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })
  const versions = useQuery({ queryKey: ["config"], queryFn: api.configVersions })

  return (
    <Async state={registry}>
      {(spec) => (
        <Async state={versions}>
          {(page) => (
            <Editor
              registry={spec}
              document={(page.items[0]?.config as Record<string, unknown>) ?? {}}
              version={page.items[0]?.version ?? null}
              history={page.items}
            />
          )}
        </Async>
      )}
    </Async>
  )
}

function Editor({
  registry,
  document,
  version,
  history,
}: {
  registry: Registry
  document: Record<string, unknown>
  version: number | null
  history: readonly ConfigVersionSummary[]
}) {
  const draft = useDraft(document, registry, version)
  const [active, setActive] = useState(() => draft.countries[0]?.code ?? "PL")

  const country = draft.countries.find((entry) => entry.code === active) ?? draft.countries[0]
  if (country === undefined) return null

  function update(code: string, questionId: string, next: Partial<EditorQuestion>) {
    draft.setCountries(
      draft.countries.map((entry) =>
        entry.code === code
          ? {
              ...entry,
              questions: entry.questions.map((set) =>
                set.questionId === questionId ? { ...set, ...next } : set,
              ),
            }
          : entry,
      ),
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Konfiguracja"
        actions={
          <>
            {draft.changes.length > 0 && (
              <Badge tone="brand" glyph="●">
                {draft.changes.length} {plural(draft.changes.length, "zmiana", "zmiany", "zmian")}
              </Badge>
            )}
            <span className="font-mono text-[13px] text-muted-foreground">
              {version === null ? "brak wersji" : `wersja ${version}`}
            </span>
          </>
        }
      />

      <Tabs value={country.code} onValueChange={setActive} className="flex flex-col gap-5">
        {/*
         * Rule sets are per country because the registries are. Which
         * countries exist at all is a property of the system rather than of
         * one set, so it is set in Ustawienia — this strip only chooses.
         */}
        <TabsList aria-label="Kraje">
          {draft.countries.map((entry) => (
            <TabsTrigger key={entry.code} value={entry.code} className="font-mono">
              <Flag code={entry.code} />
              {entry.code}
              <span className="font-sans text-xs text-muted-foreground">{enabledCount(entry)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {draft.countries.map((entry) => (
          <TabsContent key={entry.code} value={entry.code} className="flex flex-col gap-5">
            <QuestionSets
              country={entry}
              registry={registry}
              changes={draft.changes}
              onChange={(questionId, next) => update(entry.code, questionId, next)}
            />
          </TabsContent>
        ))}
      </Tabs>

      <Section title="Źródła" hint="Koszt źródła ustala grupę każdej reguły, która je czyta.">
        <ProviderList
          registry={registry}
          providers={draft.providers}
          onChange={draft.setProviders}
        />
      </Section>

      <PublishPanel draft={draft} version={version} />

      <History history={history} registry={registry} />
    </div>
  )
}

/**
 * One country's rule sets, one per question this build answers.
 *
 * A rule set belongs to a question, so the strip says which question is being
 * edited — with one question it has one tab, and it is still there, because a
 * person changing what the system answers should see *what question* they are
 * changing the answer to rather than discover it later.
 */
function QuestionSets({
  country,
  registry,
  changes,
  onChange,
}: {
  country: EditorCountry
  registry: Registry
  changes: readonly ConfigChange[]
  onChange: (questionId: string, next: Partial<EditorQuestion>) => void
}) {
  const [active, setActive] = useState(() => country.questions[0]?.questionId ?? "")
  const current = country.questions.find((set) => set.questionId === active) ?? country.questions[0]
  if (current === undefined) return null

  return (
    <Tabs value={current.questionId} onValueChange={setActive} className="flex flex-col gap-5">
      <TabsList aria-label="Pytania">
        {country.questions.map((set) => (
          <TabsTrigger key={set.questionId} value={set.questionId}>
            {set.title}
            <span className="text-xs text-muted-foreground">
              {set.rules.filter((rule) => rule.enabled).length}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      {country.questions.map((set) => (
        <TabsContent key={set.questionId} value={set.questionId} className="flex flex-col gap-5">
          <Scoreboard set={set} onChange={(next) => onChange(set.questionId, next)} />
          <Section title="Reguły" hint="W kolejności wykonania, źródło po źródle.">
            <RulesPane
              country={country.code}
              set={set}
              registry={registry}
              changes={changes}
              onChange={(next) =>
                onChange(set.questionId, {
                  rules: set.rules.map((rule) => (rule.ruleId === next.ruleId ? next : rule)),
                })
              }
              onReorder={(order) =>
                onChange(set.questionId, { rules: withEnabledOrder(set.rules, order) })
              }
            />
          </Section>
        </TabsContent>
      ))}
    </Tabs>
  )
}

/**
 * Version history, and **what each version actually changed**. A list of notes
 * is only as good as whoever wrote them; the diff against the version before is
 * the part nobody can forget to fill in.
 */
function History({
  history,
  registry,
}: {
  history: readonly ConfigVersionSummary[]
  registry: Registry
}) {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <Section title="Historia">
      <Table>
        <thead>
          <tr>
            <Th className="w-24">Wersja</Th>
            <Th>Autor</Th>
            <Th>Kiedy</Th>
            <Th>Notatka</Th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, index) => {
            const previous = history[index + 1]
            const changes = describeChanges(previous?.config ?? {}, entry.config, registry)
            const expanded = open === entry.version

            return [
              <Tr key={entry.version}>
                <Td className="p-0">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-label={`Zmiany w wersji ${entry.version}`}
                    className="flex w-full items-center gap-1.5 px-3.5 py-2.5 text-left font-mono text-[13px] font-medium"
                    onClick={() => setOpen(expanded ? null : entry.version)}
                  >
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        "size-3.5 text-muted-foreground/60 transition-transform",
                        expanded && "rotate-180",
                      )}
                    />
                    {entry.version}
                    <span className="font-sans text-xs font-normal text-muted-foreground">
                      {changes.length}
                    </span>
                  </button>
                </Td>
                <Td className="text-muted-foreground">{entry.author}</Td>
                <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
                  {formatDate(entry.createdAt)}
                </Td>
                <Td>{entry.note ?? <span className="text-muted-foreground">—</span>}</Td>
              </Tr>,
              expanded ? (
                <tr key={`${entry.version}-diff`} className="border-b last:border-0">
                  <Td colSpan={4} className="bg-muted/30 px-3.5 py-3">
                    {changes.length === 0 ? (
                      <p className="text-[13px] text-muted-foreground">
                        {previous === undefined
                          ? "Pierwsza wersja: tyle, ile dawały wbudowane domyślne."
                          : "Bez zmian względem poprzedniej wersji."}
                      </p>
                    ) : (
                      <ul className="space-y-1">
                        {changes.map((change) => (
                          <li key={`${change.scope}·${change.label}`} className="text-[13px]">
                            <span className="text-muted-foreground">{change.scope} · </span>
                            {change.label}:{" "}
                            <span className="text-muted-foreground line-through">
                              {change.from}
                            </span>{" "}
                            <span className="font-medium">{change.to}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Td>
                </tr>
              ) : null,
            ]
          })}
        </tbody>
      </Table>
    </Section>
  )
}
